import { isEmpty } from "lodash";
import {
  isTypeCodeableConcept,
  isTypeCoding,
} from "../../../types/codeableConcept";
import { isOptionInCurrentValue } from "../../../utils/optionSearchMatch";

/**
 * Converts a persisted form value (Coding, Reference, CodeableConcept, etc.)
 * into the option shape expected by TypeAhead Autocomplete, without requiring
 * a prior API search result.
 */
export function valueToTypeaheadOption(
  value: unknown,
  optionType: string,
): unknown | null {
  if (value == null || value === "") return null;

  const record = value as Record<string, unknown>;

  if (optionType === "snomed") {
    if (isTypeCodeableConcept(value)) {
      const coding = record.coding as { code?: string; display?: string }[];
      return {
        snomedCID: coding[0]?.code,
        snomedFSN: coding[0]?.display,
      };
    }
    if (isTypeCoding(value)) {
      return { snomedCID: record.code, snomedFSN: record.display };
    }
    if (record.snomedCID != null) return value;
    if (record.code != null) {
      return {
        snomedCID: record.code,
        snomedFSN: record.display ?? record.code,
      };
    }
  }

  if (record.type === "medications" && record.identifier != null) {
    return {
      medicationCode: record.identifier,
      // Keep `code` aligned with other reference→option shapes so multi-select
      // remappers that read `v.code` (e.g. MedicationsInput) retain prior picks.
      code: record.identifier,
      medicineName: record.display,
      display: record.display,
    };
  }

  if (isTypeCodeableConcept(value)) {
    const coding = record.coding as { code?: string; display?: string }[];
    return { code: coding[0]?.code, display: coding[0]?.display };
  }

  if (isTypeCoding(value)) {
    return { code: record.code, display: record.display };
  }

  if (
    record.identifier != null &&
    record.display != null &&
    !record.snomedCID
  ) {
    return {
      medicationCode: record.identifier,
      medicineName: record.display,
      display: record.display,
      code: record.identifier,
    };
  }

  if (record.code != null || record.display != null) return value;

  return value;
}

export function valuesToTypeaheadOptions(
  values: unknown[],
  optionType: string,
): unknown[] {
  return values
    .map((value) => valueToTypeaheadOption(value, optionType))
    .filter((value): value is NonNullable<typeof value> => value != null);
}

export function resolveTypeaheadOption(
  value: unknown,
  options: unknown[],
  optionType: string,
  findInOptions: (value: unknown, options: unknown[]) => unknown | undefined,
): unknown | null {
  if (isEmpty(value)) return null;

  if (options.length > 0) {
    const fromOptions = findInOptions(value, options);
    if (fromOptions) return fromOptions;
  }

  return valueToTypeaheadOption(value, optionType);
}

export function mergeCurrentValueIntoDisplayOptions<T>({
  options,
  currentValue,
  multiple,
  isOptionEqualToValue,
}: {
  options: T[];
  currentValue: unknown;
  multiple?: boolean;
  isOptionEqualToValue?: (option: T, value: unknown) => boolean;
}): T[] {
  if (currentValue == null) return options;

  const selectedValues = multiple
    ? Array.isArray(currentValue)
      ? currentValue
      : []
    : [currentValue];

  if (selectedValues.length === 0) return options;

  const missingSelections = selectedValues.filter((selected) => {
    if (selected == null) return false;

    return !options.some((option) => {
      if (isOptionEqualToValue?.(option, selected)) return true;
      return isOptionInCurrentValue(
        option as Record<string, unknown>,
        selected,
        multiple,
      );
    });
  });

  if (missingSelections.length === 0) return options;

  return [...(missingSelections as T[]), ...options];
}
